/**
 * Slug preview + validation for the category form.
 *
 * Mirrors core-api's src/services/admin/games/category-slug.ts. This copy only
 * previews and pre-validates — the backend remains the authority, so an empty
 * field is submitted as "no slug" rather than as this function's guess.
 */

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
export const MAX_SLUG_LENGTH = 64

export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "all",
  "api",
  "categories",
  "category",
  "game",
  "games",
  "lobby",
  "provider",
  "providers",
  "robots.txt",
  "search",
  "sitemap.xml"
])

const TRANSLITERATIONS: Record<string, string> = {
  ß: "ss",
  æ: "ae",
  ø: "o",
  å: "a",
  đ: "d",
  ð: "d",
  þ: "th",
  ł: "l",
  œ: "oe"
}

export function slugify(input: string): string {
  if (typeof input !== "string") return ""

  const transliterated = Array.from(input.toLowerCase())
    .map((char) => TRANSLITERATIONS[char] ?? char)
    .join("")

  const slug = transliterated
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")

  if (slug.length <= MAX_SLUG_LENGTH) return slug

  const clamped = slug.slice(0, MAX_SLUG_LENGTH)
  const lastDash = clamped.lastIndexOf("-")
  return (lastDash > 0 ? clamped.slice(0, lastDash) : clamped).replace(/-+$/, "")
}

/** Keeps the input field on legal characters while the admin types. */
export function normalizeSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .slice(0, MAX_SLUG_LENGTH)
}

export type SlugProblem = "invalid" | "reserved" | null

export function validateSlug(slug: string): SlugProblem {
  if (!slug) return null // empty means "derive it on the server"
  if (!SLUG_PATTERN.test(slug) || slug.length > MAX_SLUG_LENGTH) return "invalid"
  if (RESERVED_SLUGS.has(slug)) return "reserved"
  return null
}

/**
 * The URL players will see. Falls back to a relative path when the admin
 * deployment has no REACT_APP_PLAYER_SITE_URL configured.
 */
export function playerCategoryUrl(slug: string): string {
  const path = `/en/casino/${slug}`
  const origin = process.env.REACT_APP_PLAYER_SITE_URL?.replace(/\/+$/, "")
  return origin ? `${origin}${path}` : path
}
